<script lang="ts">
  import Loading from '../../loader.svelte';
  import RepositoryFactory, { CONTRACT, MNP } from '../../../repositories/RepositoryFactory';
  import type { GetContractResult } from '../../../repositories/contract';
  import type { GetMnpReserveNumberForPremiumResult } from '../../../repositories/mnp';
  import { loginUser } from '../../../components/auth/LoginUser';
  import MnpModal from './MnpReserveNumberPremiumModal.svelte';
  import { getContext } from 'svelte';
  import { isAfter, isBefore, addHours, addDays, endOfMonth, startOfMonth, parse } from 'date-fns';

  const { close }: any = getContext('simple-modal');

  const terminateReasons = [
    { code: 'QUALITY', name: '品質' },
    { code: 'FEE', name: '料金' },
    { code: 'SUPPORT', name: 'サポート' },
    { code: 'OTHER', name: 'その他' }
  ];
  let terminateReasonCode: 'QUALITY' | 'FEE' | 'SUPPORT' | 'OTHER' = 'QUALITY';
  let terminateReasonDetail: string = '';

  let pageStatus: 'WITHDRAWAL_OR_MNP' | 'TOP' | 'INPUT' | 'CONFIRM' | 'COMPLETE' | 'MNP' = 'WITHDRAWAL_OR_MNP';

  let promise = initialize();
  let contract: GetContractResult;
  let mnp: GetMnpReserveNumberForPremiumResult;
  let terminateDate: string;

  async function initialize() {
    const contractRepository = RepositoryFactory[CONTRACT];
    const mnpRepository = RepositoryFactory[MNP];
    await Promise.all([contractRepository.getContract(), mnpRepository.getMnpReserveNumberForPremium()])
      .then((res) => {
        if (!res) {
          throw new Error();
        }
        contract = res[0];
        mnp = res[1];
        decidePageStatus(contract);
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
    return contract;
  }

  async function requestTerminate() {
    const contractRepository = RepositoryFactory[CONTRACT];
    await contractRepository
      .terminateContract({
        terminateTypeCode: 'APPLIED',
        terminateReasonCode: terminateReasonCode,
        terminateReasonDetail: terminateReasonDetail
      })
      .then((res) => {
        terminateDate = res.terminateDate;
        transisionWithdrawalComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }

  function displayErrorAlert(status: number) {
    if (status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      location.reload();
    } else {
      alert('エラーが発生しました。再度やり直してください。');
    }
  }

  /**
   * 画面遷移情報設定
   */
  function handleMnpReserverNumberRequest() {
    pageStatus = 'MNP';
  }

  function handleWithdrawalTop() {
    pageStatus = 'TOP';
    terminateReasonCode = 'QUALITY';
    terminateReasonDetail = '';
  }

  function handleWithdrawalInput() {
    pageStatus = 'INPUT';
  }

  function handleWithdrawalComfirm() {
    pageStatus = 'CONFIRM';
  }

  function handleWithdrawalComplete() {
    if (isTheBeginningOfTheEndOfTheMonth()) {
      alert('解約は月末日22時以降はできません');
      return;
    }
    if (isUnAvailableTerminate()) {
      alert('MNP予約番号発行中または有効期限翌日までは解約できません。');
      return;
    }
    requestTerminate();
  }

  function handleWithdrawalCompleteClose() {
    close();
    location.reload();
  }

  function transisionWithdrawalComplete() {
    pageStatus = 'COMPLETE';
  }

  function isTheBeginningOfTheEndOfTheMonth() {
    const now = new Date();
    // 月末22時 <= 現在日時 OR 月初0時 > 現在日時
    return isBefore(addHours(endOfMonth(now), 22), now) || isAfter(addHours(startOfMonth(now), 0), now);
  }

  function isUnAvailableTerminate() {
    if (!mnp || ['ACCEPT_ERROR', 'ISSUE_ERROR', 'CANCEL'].includes(mnp.issueStatusCode)) {
      return false;
    }

    if (
      mnp.issueStatusCode == 'EXPIRED' &&
      // 有効期限 + 1 < 現在日
      isBefore(addDays(parse(mnp.expirationDate, 'yyyy-MM-dd', new Date()), 1), new Date().setHours(0, 0, 0, 0))
    ) {
      return false;
    } else {
      return true;
    }
  }

  function decidePageStatus(contract: GetContractResult) {
    if (['TOP', 'INPUT', 'CONFIRM', 'COMPLETE'].includes(pageStatus)) {
      return;
    }
    if (contract.serviceClass == 'VOICE_PLAN') {
      pageStatus = 'WITHDRAWAL_OR_MNP';
      return;
    }
    if (['DATA_PLAN', 'SMS_PLAN'].includes(contract.serviceClass)) {
      pageStatus = 'TOP';
      return;
    }
  }
</script>

{#await promise}
  <Loading />
{:then contract}
  {#if pageStatus === 'MNP'}<!-- MNP転出予約画面表示-->
    <MnpModal />
  {:else if pageStatus === 'WITHDRAWAL_OR_MNP'}<!-- MNP転出予約-解約選択 -->

    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">解約</h1>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">ご契約いただいている以下内容を解約します。</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">{contract.serviceName}</span>
        </div>
      </div>

      <div class="flex-1">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button
              class="button-primary mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED'}
              on:click={handleWithdrawalTop}
            >
              解約手続きへ
            </button>
            <button
              class="button-primary md:ml-5 mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED' ||
                (contract.contractStatusCode === 'SUSPEND' && contract.suspendReasonCode !== 'APPLIED')}
              on:click={handleMnpReserverNumberRequest}
            >
              MNP予約番号発行
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'TOP'}<!-- 解約 TOP-->
    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">解約</h1>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">ご契約いただいている以下内容を解約します。</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">{contract.serviceName}</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">注意事項等をご確認いただき、「手続きへ」を押してください。</span>
        </div>
      </div>

      <div class="flex-1 mb-10">
        <div class="shadow rounded-lg bg-gray-200 border-1 m-5 p-4">
          <div class="flex flex-col">
            <div class="flex-1">
              <div class="justify-self-start w-full inline-block">
                <ul>
                  <li>注意事項</li>
                  <li>・「解約する」ボタンを押した後のキャンセルはできません</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button
              on:click|once={handleWithdrawalInput}
              class="button-primary mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED'}
            >
              手続きへ
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'INPUT'}<!-- 解約 入力-->

    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">解約</h1>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="m-5">
        <span class="form-label-title mb-1 required">解約理由</span>
        <div class="flex flex-col w-full md:flex-row md:space-x-0 lg:space-x-4">
          {#each terminateReasons as item}
            <label class="flex items-center">
              <input type="radio" name="terminateReason" value={item.code} bind:group={terminateReasonCode} />
              <span class="text-sm ml-1">{item.name}</span>
            </label>
          {/each}
        </div>
      </div>
      <div class="m-5">
        <div class="form-label-title mb-1">解約理由詳細</div>
        <textarea
          class="w-full p-2 border focus:ring-0 focus:ring-blue-200 focus:border-transparent text-sm"
          bind:value={terminateReasonDetail}
        />
      </div>
      <div class="flex-1">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button on:click|once={handleWithdrawalTop} class="button-secondary mb-5"> 戻る </button>
            <button
              class="button-primary md:ml-5 mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED'}
              on:click={handleWithdrawalComfirm}
            >
              確認する
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'CONFIRM'}<!-- 解約 確認-->
    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">解約</h1>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">この内容で本当に解約しますか？</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">{contract.serviceName}</span>
        </div>
      </div>

      <div class="m-5">
        <span class="form-label-title">解約理由</span>
        <div class="flex space-x-4 w-full mt-1">
          <div class="label-value">
            {terminateReasons.filter((reason) => reason.code === terminateReasonCode)[0].name}
          </div>
        </div>
      </div>
      <div class="m-5">
        <div class="form-label-title">解約理由詳細</div>
        <div class="label-value">{terminateReasonDetail ?? '-'}</div>
      </div>
      <div class="flex-1 mt-8">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button on:click|once={handleWithdrawalInput} class="button-secondary mb-5"> 戻る </button>
            <button on:click|once={handleWithdrawalComplete} class="button-primary md:ml-5 mb-5"> 解約する </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'COMPLETE'}<!-- 解約 完了-->
    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">解約</h1>
      </div>
    </div>
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-5">
        <div class="mb:font-sm">ご利用ありがとうございました。</div>
        <div class="mb:font-sm mt-3">解約日：{terminateDate}</div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">マイページは解約後、一定期間過ぎると利用できなくなるためご注意ください。</span>
        </div>
      </div>

      <div class="flex flex-col justify-items-center items-center">
        <div class="flex-1 m-5">
          <button on:click|once={handleWithdrawalCompleteClose} class="button-primary"> 閉じる </button>
        </div>
      </div>
    </div>
  {/if}
{/await}

<style lang="postcss">
  .required:after {
    content: ' *';
    color: red;
  }

  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
  .button-secondary {
    @apply bg-gray-300 rounded hover:bg-gray-400 text-sm font-semibold text-gray-500 focus:outline-none px-10 py-2;
  }
  .button-secondary:disabled {
    @apply opacity-30 cursor-default;
  }
  .form-label-title {
    @apply text-sm font-semibold text-gray-700;
  }
  .label-value {
    @apply text-sm text-gray-700 leading-5;
  }
</style>
